// Modo demo: enmascara datos de clientes (nombres/contacto/negocio) y montos en
// TODAS las respuestas de Apollo. Solo para usuarios internos (@laiten.app /
// @gravityepoch.com) y se activa con ?demo=1 en la URL (o el toggle interno).
// Nunca toca el backend ni los datos reales: solo transforma la respuesta.

const INTERNAL_DOMAINS = ['laiten.app', 'gravityepoch.com']
const DEMO_KEY = 'demoMode'

export const getUserEmail = () => (localStorage.getItem('email') || '').trim().toLowerCase()

export const isInternalUser = () => {
  const email = getUserEmail()
  return !!email && INTERNAL_DOMAINS.some((d) => email.endsWith('@' + d))
}

export const isDemoFlagOn = () => localStorage.getItem(DEMO_KEY) === '1'
export const setDemoFlag = (on) => { if (on) localStorage.setItem(DEMO_KEY, '1'); else localStorage.removeItem(DEMO_KEY) }
// Doble candado: aunque el flag esté prendido, solo aplica a usuarios internos
export const demoActive = () => isInternalUser() && isDemoFlagOn()

// Activación por URL (?demo=1 / ?demo=0), gateada a internos
export const syncDemoFromUrl = () => {
  try {
    const p = new URLSearchParams(window.location.search)
    if (!p.has('demo')) return
    if (!isInternalUser()) return
    const v = (p.get('demo') || '').toLowerCase()
    setDemoFlag(v === '' || v === '1' || v === 'true' || v === 'on')
  } catch (e) { /* noop */ }
}

// ── Datos falsos deterministas (mismo original → mismo falso) ──
const FIRST = ['María', 'Carlos', 'Ana', 'Luis', 'Sofía', 'Jorge', 'Valeria', 'Diego', 'Fernanda', 'Ricardo', 'Paola', 'Andrés', 'Gabriela', 'Miguel', 'Regina', 'Emilio', 'Daniela', 'Rodrigo', 'Ximena', 'Alejandro', 'Natalia', 'Héctor', 'Mariana', 'Pablo', 'Renata']
const LAST = ['González', 'Ramírez', 'Hernández', 'López', 'Martínez', 'Torres', 'Flores', 'Vázquez', 'Castro', 'Ortega', 'Guerrero', 'Mendoza', 'Rojas', 'Domínguez', 'Cabrera', 'Ibarra', 'Ríos', 'Núñez', 'Salazar', 'Peña', 'Cordero', 'Fuentes', 'Cervantes', 'Zamora', 'Bautista']
const PLACES = ['Club Deportivo', 'Residencial Las Palmas', 'Villa Serena', 'Torre Norte', 'Casa Club', 'Parque Central', 'Condominio Roble', 'Fraccionamiento El Lago']
const TITLES = ['Aviso general', 'Recordatorio', 'Comunicado', 'Mantenimiento programado', 'Notificación']
const LETTERS = 'ABCDEFGHJKLMNPRSTUVWXYZ'

const hashStr = (s) => {
  const str = String(s == null ? '' : s)
  let h = 5381
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) | 0
  return Math.abs(h)
}
const pick = (arr, seed) => arr[seed % arr.length]
const stripAccents = (s) => s.normalize('NFD').replace(/[̀-ͯ]/g, '')
const letters = (o, n) => { let s = ''; let h = hashStr(o); for (let i = 0; i < n; i++) { s += LETTERS[h % LETTERS.length]; h = Math.floor(h / 7) + 13 } return s }
const digits = (o, n) => { const base = hashStr(o) % Math.pow(10, n); return String(base).padStart(n, '0') }

const fakeFirst = (o) => pick(FIRST, hashStr(o))
const fakeLast = (o) => pick(LAST, hashStr('L' + o))
const fakeFull = (o) => `${fakeFirst(o)} ${fakeLast(o)}`
const fakePlace = (o) => `${pick(PLACES, hashStr(o))} ${100 + (hashStr(o) % 900)}`
const fakeCompany = (o) => `Grupo ${fakeLast(o)} S.A. de C.V.`
const fakeEmail = (o) => stripAccents(`${fakeFirst(o).toLowerCase()}.${fakeLast(o).toLowerCase()}@ejemplo.com`)
const fakePhone = (o) => `55${digits(o, 8)}`
const fakeFolio = (o) => `A-${digits(o, 6)}`
const fakeRef = (o) => `REF-${digits(o, 7)}`
const fakeUuid = (o) => `${letters(o, 8)}-${digits(o, 4)}-${digits('u' + o, 4)}-${letters('v' + o, 4)}`.toLowerCase()
const fakeRfc = (o) => `${letters(o, 4)}${digits(o, 6)}${letters('r' + o, 3)}`
const fakePlates = (o) => `${letters(o, 3)}-${digits(o, 3)}`
const fakeAddress = (o) => `Calle Ficticia ${100 + (hashStr(o) % 900)}, Col. Centro`
const fakeTitle = (o) => pick(TITLES, hashStr(o))
const fakeContent = () => 'Contenido de ejemplo (modo demo).'
const mask4 = () => '••••'
// Monto falso plausible e independiente: conserva el orden de magnitud (para que se
// vea creíble) pero con dígitos pseudo-aleatorios por hash. Determinista (mismo real
// → mismo falso). No se puede inferir el valor real; los totales no cuadran exacto.
const fakeMoney = (n) => {
  const v = Math.abs(n)
  if (!v) return 0
  const mag = Math.pow(10, Math.floor(Math.log10(v)))
  const mult = 1 + (hashStr(String(n)) % 900) / 100 // 1.00 .. 9.99
  const step = mag >= 1000 ? 10 : (mag >= 100 ? 5 : 1)
  const out = Math.round((mag * mult) / step) * step
  return n < 0 ? -out : out
}

// ── Llaves a enmascarar (por nombre de campo) ──
const FULLNAME_KEYS = new Set(['fullName', 'userName', 'providerName', 'clientName', 'guestName', 'representative'])
const FIRSTNAME_KEYS = new Set(['userRoleName'])
const LASTNAME_KEYS = new Set(['apellidoPaterno', 'apellidoMaterno'])
const PLACE_KEYS = new Set(['propertyName', 'toProperty', 'fromProperty'])
const COMPANY_KEYS = new Set(['razonSocial', 'legal_name'])
const EMAIL_KEYS = new Set(['email', 'userEmail'])
const PHONE_KEYS = new Set(['phone', 'userPhone'])
const FOLIO_KEYS = new Set(['folio', 'invoiceNumber'])
const REF_KEYS = new Set(['reference'])
const UUID_KEYS = new Set(['uuid'])
const RFC_KEYS = new Set(['rfc', 'tax_id'])
const ADDRESS_KEYS = new Set(['address'])
const PLATES_KEYS = new Set(['licensePlates'])
// Fotos de personas/vehículos/identificaciones (accesos, usuarios): se sustituyen
// por un recuadro gris para no exponer imágenes reales en demo
const GRAY_IMAGE = 'data:image/svg+xml;utf8,' + encodeURIComponent("<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><rect width='100%' height='100%' fill='#c4c9cf'/></svg>")
const IMAGE_KEYS = new Set(['fotoFilelink', 'photoFilelink', 'identificationFilelink', 'ineFilelink'])
// `fileLink` solo cuando cuelga de un objeto imagen (p.ej. getAccess.image.fileLink)
const IMAGE_LINK_PARENT = new Set(['image'])
const SECRET_KEYS = new Set(['pin', 'key', 'code', 'serie'])
const TITLE_KEYS = new Set(['title'])
const CONTENT_KEYS = new Set(['text', 'description', 'observations'])
const PROVIDER_KEYS = new Set(['provider']) // vendedor (persona) en reportes

const MONEY_KEYS = new Set(['amount', 'total', 'toPay', 'totalPaid', 'penaltyFee', 'price', 'cost', 'unitPrice', 'averagePrice', 'totalAmount', 'totalOverdueAmount', 'balance', 'initialBalance', 'startingBalance', 'remainingBalance', 'res', 'subtotal', 'freeReservations', 'income', 'expense', 'invoice', 'notPaidInvoice', 'bill', 'notPaidBill', 'profit'])
const MONEY_ARRAY_KEYS = new Set(['months'])

// `.name` es PII cuando cuelga de estas relaciones (persona / lugar / negocio)
const PERSON_NAME_PARENT = new Set(['user', 'userRole', 'externalUser', 'guest', 'fromClient', 'client', 'toClient', 'customer', 'userRoles'])
const PLACE_NAME_PARENT = new Set(['living', 'property', 'fromProperty', 'toProperty', 'properties', 'invoices', 'invoice'])
const CATEGORY_NAME_PARENT = new Set(['accountingCategory'])

const maskScalar = (key, val, parentKey) => {
  if (val == null) return val
  if (typeof val === 'string') {
    if (key === 'name') {
      if (PERSON_NAME_PARENT.has(parentKey)) return fakeFull(val)
      if (PLACE_NAME_PARENT.has(parentKey)) return fakePlace(val)
      if (CATEGORY_NAME_PARENT.has(parentKey)) return `Categoría ${letters(val, 3)}`
      return val
    }
    if (FULLNAME_KEYS.has(key)) return fakeFull(val)
    if (FIRSTNAME_KEYS.has(key)) return fakeFirst(val)
    if (LASTNAME_KEYS.has(key)) return fakeLast(val)
    if (PROVIDER_KEYS.has(key)) return fakeFull(val)
    if (PLACE_KEYS.has(key)) return fakePlace(val)
    if (COMPANY_KEYS.has(key)) return fakeCompany(val)
    if (EMAIL_KEYS.has(key)) return fakeEmail(val)
    if (PHONE_KEYS.has(key)) return fakePhone(val)
    if (FOLIO_KEYS.has(key)) return fakeFolio(val)
    if (REF_KEYS.has(key)) return fakeRef(val)
    if (UUID_KEYS.has(key)) return fakeUuid(val)
    if (RFC_KEYS.has(key)) return fakeRfc(val)
    if (ADDRESS_KEYS.has(key)) return fakeAddress(val)
    if (PLATES_KEYS.has(key)) return fakePlates(val)
    if (val && IMAGE_KEYS.has(key)) return GRAY_IMAGE
    if (val && key === 'fileLink' && IMAGE_LINK_PARENT.has(parentKey)) return GRAY_IMAGE
    if (SECRET_KEYS.has(key)) return mask4(val)
    if (TITLE_KEYS.has(key)) return fakeTitle(val)
    if (CONTENT_KEYS.has(key)) return fakeContent(val)
    return val
  }
  if (typeof val === 'number') {
    // `summary` trae conteos (total/active/…), no montos → no escalar
    if (MONEY_KEYS.has(key) && parentKey !== 'summary') return fakeMoney(val)
    return val
  }
  return val
}

const walk = (node, parentKey) => {
  if (Array.isArray(node)) {
    if (MONEY_ARRAY_KEYS.has(parentKey)) return node.map((x) => (typeof x === 'number' ? fakeMoney(x) : walk(x, parentKey)))
    return node.map((x) => walk(x, parentKey))
  }
  if (node && typeof node === 'object') {
    const out = {}
    for (const k in node) {
      const v = node[k]
      if (v && typeof v === 'object') out[k] = walk(v, k)
      else out[k] = maskScalar(k, v, parentKey)
    }
    return out
  }
  return node
}

export const maskResponseData = (data) => {
  try { return walk(data, null) } catch (e) { return data }
}
